import { createTheme } from "@mui/material/styles";
import colors from "./colors";

const materialTheme = createTheme({
  palette: {
    primary: {
      main: colors.cyan.cyan500,
      light: colors.cyan.cyan300,
      dark: colors.cyan.cyan700,
      contrastText: colors.white,
    },
    secondary: {
      main: colors.purple.purple500,
    },
    error: {
      main: colors.red.red500,
    },
    warning: { main: colors.yellow.yellow500 },
    info: { main: colors.gray.gray500 },
    success: { main: colors.green.green500 },
    text: {
      primary: colors.gray.gray900,
      secondary: colors.gray.gray700,
      disabled: colors.gray.gray300,
    },
    grey: {
      50: colors.gray.gray50,
      100: colors.gray.gray100,
      200: colors.gray.gray200,
      300: colors.gray.gray300,
      400: colors.gray.gray400,
      500: colors.gray.gray500,
      600: colors.gray.gray600,
      700: colors.gray.gray700,
      800: colors.gray.gray800,
      900: colors.gray.gray900,
    },
  },
  typography: {
    fontFamily: ['"IBM Plex Sans"', '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
  },

  // The theme will need more planning and work to be fully functional. For now, we
  // are choosing to style only the components needed to implement the DatePicker
  // component. This will need to be revisited in the future before we begin building
  // out more components.
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          transform: "none",
          marginBottom: "8px",
          position: "relative",
          color: colors.gray.gray900,
          variants: [
            {
              props: { size: "small" },
              style: {
                fontSize: "14px",
                lineHeight: "16px",
              },
            },
            {
              props: { size: "normal" },
              style: {
                fontSize: "16px",
                lineHeight: "20px",
              },
            },
          ],
          "&.MuiInputLabel-root.Mui-focused": { color: colors.gray.gray900 },
          "&.MuiInputLabel-root.Mui-disabled": { color: colors.gray.gray300 },
          "&.MuiInputLabel-root.Mui-error": { color: colors.red.red500 },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          border: "2px solid",
          borderColor: colors.gray.gray500,
          color: colors.gray.gray500,
          backgroundColor: colors.white,
          fontWeight: 400,
          variants: [
            {
              props: { size: "small" },
              style: {
                height: "36px",
                fontSize: "14px",
                lineHeight: "20px",
              },
            },
            {
              props: { size: "medium" },
              style: {
                height: "48px",
                fontSize: "16px",
                lineHeight: "24px",
              },
            },
          ],
          "& input::placeholder": {
            color: colors.gray.gray500,
            opacity: 1,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&.MuiInputBase-root:not(.Mui-focused):not(.Mui-error):not([value=''])": {
            color: colors.gray.gray900,
          },
          "&.MuiInputBase-root.Mui-focused": {
            border: "3px solid",
            backgroundColor: colors.gray.gray50,
            color: colors.gray.gray700,
            borderColor: colors.gray.gray900,
          },
          "&.MuiInputBase-root.Mui-disabled": {
            color: colors.gray.gray300,
            borderColor: colors.gray.gray300,
          },
          "&.MuiInputBase-root.Mui-error": {
            color: colors.gray.gray500,
            borderColor: colors.red.red500,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "20px",
          color: colors.gray.gray500,
          "&.MuiFormHelperText-filled.Mui-disabled": {
            color: colors.gray.gray300,
          },
          "&.MuiFormHelperText-filled.Mui-error": {
            color: colors.red.red500,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: colors.white,
          "& .MuiPickersCalendarHeader-label": {
            lineHeight: "20px",
          },
          "& .MuiDayCalendar-weekDayLabel, & .MuiPickersDay-root": {
            fontSize: "14px",
            lineHeight: "20px",
          },
          "& .MuiDateRangePickerDay-rangeIntervalDayHighlight:not(.MuiDateRangePickerDay-hiddenDayFiller)":
            {
              backgroundColor: colors.gray.gray50,
            },

          "& .MuiDateRangePickerDay-day.Mui-selected": {
            color: colors.white,
            backgroundColor: colors.gray.gray900,
          },
        },
      },
    },
    // eslint-disable-next-line
    // @ts-ignore MuiPickersToolbar is a valid property but not being recognized
    MuiPickersToolbar: {
      styleOverrides: {
        root: {
          "& .MuiTypography-root.MuiTypography-overline": {
            color: colors.gray.gray500,
            fontSize: "16px",
            fontWeight: 500,
            lineHeight: "20px",
          },
          "& .MuiPickersToolbar-content .MuiTypography-root": {
            color: colors.gray.gray500,
            fontSize: "32px",
            fontWeight: 600,
            lineHeight: "40px",
          },
          "& .MuiPickersToolbar-content .Mui-selected": {
            color: colors.gray.gray900,
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          "&.MuiDialogActions-root .MuiButtonBase-root": {
            fontSize: "16px",
            fontWeight: 500,
            lineHeight: "20px",
          },
        },
      },
    },
  },
});

export type Theme = typeof materialTheme;

export default materialTheme;
